/**
 * Text-level diff helpers for the source view's non-clobbering live-refresh
 * path. Pure functions returning CM6-shaped `{from, to, insert}` ops; the
 * source view dispatches them via `view.dispatch({ changes })`.
 */
import { calcPatch } from 'fast-myers-diff';

export type TextDiff = {
	from: number;
	insert: string;
	to: number;
};

/**
 * Character-level diff returning a list of disjoint replace hunks in old-text
 * coords. Uses linear-space Myers (via `fast-myers-diff.calcPatch`), so
 * multi-region edits are kept separate rather than being collapsed into a
 * single bounding span.
 */
export const diffHunks = (oldText: string, newText: string): TextDiff[] => {
	const hunks: TextDiff[] = [];
	for (const [from, to, insert] of calcPatch(oldText, newText)) {
		hunks.push({ from, to, insert });
	}
	return hunks;
};

/**
 * Produce buffer-coord change ops that apply the remote edit (`seed → next`)
 * without disturbing the user's unsynced typing (`seed → buffer`). Returns:
 *   - `[]` when there is no remote change (caller still bumps seed)
 *   - `[hunk, ...]` when every remote hunk is disjoint from every user hunk
 *     (each remote hunk is shifted through preceding user hunks' deltas)
 *   - `null` when any pair of hunks overlaps (caller defers)
 *
 * Touching ranges count as disjoint; strict interior overlap, or two concurrent
 * zero-width inserts at the same position, count as a collision.
 */
export const tryMergeDisjointDiffs = (
	seed: string,
	buffer: string,
	next: string,
): TextDiff[] | null => {
	const remoteHunks = diffHunks(seed, next);
	if (remoteHunks.length === 0) {
		return [];
	}
	const userHunks = diffHunks(seed, buffer);
	if (userHunks.length === 0) {
		return remoteHunks;
	}
	for (const rh of remoteHunks) {
		for (const uh of userHunks) {
			if (rangesOverlap(rh, uh)) {
				return null;
			}
		}
	}
	return remoteHunks.map((rh) => {
		let delta = 0;
		for (const uh of userHunks) {
			if (uh.to <= rh.from) {
				delta += uh.insert.length - (uh.to - uh.from);
			}
		}
		return { from: rh.from + delta, to: rh.to + delta, insert: rh.insert };
	});
};

const rangesOverlap = (a: TextDiff, b: TextDiff): boolean => {
	// Strict interior overlap.
	if (a.from < b.to && b.from < a.to) {
		return true;
	}
	// Concurrent zero-width inserts at the same position: ambiguous order.
	if (a.from === b.from && a.to === a.from && b.to === b.from) {
		return true;
	}
	return false;
};
